<div class="wizard">

  <header class="wizard__header">
    <button type="button" class="back-link" (click)="cancel()">
      <svg viewBox="0 0 24 24" aria-hidden="true"><path [attr.d]="iconPath('back')"></path></svg>
      Agent Studio
    </button>
    <div class="wizard__title-row">
      <h1 class="wizard__title">{{ pageTitle }}</h1>
      @if (copyMode) {
        <span class="tag tag--copy">Account copy</span>
      } @else if (draftId) {
        <span class="tag">Draft saved</span>
      }
    </div>
    <p class="wizard__subtitle">{{ copyMode
      ? 'Changes apply only to this account\'s copy. The master agent and other accounts are not affected.'
      : 'Work through the steps to build, test, and publish a guided agent. Each Next saves the draft.' }}</p>
  </header>

  <div class="wizard__layout ar-card">
    <nav class="stepper" aria-label="Agent setup steps">
      <ol class="stepper__list">
        @for (s of steps; track s.key; let i = $index) {
          <li>
            <button type="button" class="stepper__item" [attr.aria-current]="step === i + 1 ? 'step' : null"
                    [class.stepper__item--active]="step === i + 1" [class.stepper__item--complete]="stepHasContent(i + 1)"
                    (click)="selectStep(i + 1)">
              <span class="stepper__marker">
                @if (stepHasContent(i + 1) && step !== i + 1) {
                  <svg viewBox="0 0 24 24" aria-hidden="true"><path [attr.d]="iconPath('check')"></path></svg>
                } @else {
                  {{ i + 1 }}
                }
              </span>
              <span class="stepper__text">
                <span class="stepper__title">{{ s.title }}</span>
                <span class="stepper__meta">{{ stepHasContent(i + 1) ? 'Done' : s.optional ? 'Optional' : 'Required' }}</span>
              </span>
            </button>
          </li>
        }
      </ol>
    </nav>

    <form class="wizard__panel" (ngSubmit)="step === stepTitles.length ? finish() : next()">
      <header class="panel-head">
        <span class="panel-head__step">Step {{ step }} of {{ steps.length }}</span>
        <h2 class="panel-head__title">{{ currentStep.title }}</h2>
        <p class="panel-head__hint">{{ currentStep.hint }}</p>
      </header>

      <div class="wizard-grid">
        @if (error) {
          <p class="error-banner wizard-grid__full" role="alert">{{ error }}</p>
        }

        @if (stepKey === 'identity') {
          <div class="of-field wizard-grid__full">
            <kx-multiselect-dropdown [questions]="domainQuestion" [settings]="selectSettings" [data]="domainOptions"
              [placeholder]="'Choose a domain'" [(ngModel)]="domain" name="domain" [disabled]="copyMode"></kx-multiselect-dropdown>
            <label>Domain*</label>
            <span class="of-hint">The application the agent is published into.</span>
          </div>
          <div class="of-field">
            <input type="text" class="of-control" placeholder="claims-assistant" [(ngModel)]="agentName" name="agentName" [disabled]="copyMode">
            <label>Agent name*</label>
            <span class="of-hint">@if (agentIdPreview) {Agent ID: <code>{{ agentIdPreview }}</code>} @else {With the domain, this makes the agent ID.}</span>
          </div>
          <div class="of-field">
            <input type="text" class="of-control" placeholder="Claims Assistant" [(ngModel)]="displayName" name="displayName">
            <label>Display name*</label>
            <span class="of-hint">What people see in lists and chats.</span>
          </div>
        }

        @if (stepKey === 'instructions') {
          <div class="of-field of-field--area wizard-grid__full">
            <textarea class="of-control" rows="3" [(ngModel)]="goal" name="goal"
                      placeholder="Review incoming customer requests, identify the issue category, and route them to the correct team."></textarea>
            <label>What should this agent do?*</label>
          </div>
          <div class="of-field of-field--area wizard-grid__full">
            <textarea class="of-control" rows="8" [(ngModel)]="instructions" name="instructions"
                      placeholder="When a request arrives: 1) identify the issue type, 2) classify severity, 3) choose the correct team, 4) return a concise recommendation."></textarea>
            <label>Instructions (system prompt)</label>
            <span class="of-hint">Falls back to the goal above when left blank.</span>
          </div>
        }

        @if (stepKey === 'model') {
          <div class="of-field">
            <kx-multiselect-dropdown [questions]="providerQuestion" [settings]="selectSettings" [data]="providerOptions"
              [placeholder]="providers.length ? 'Select a provider' : 'Loading providers…'" [ngModel]="providerName" name="providerName"
              (onSelect)="$event?.isManual && selectProvider($event.id)" (onDeSelect)="selectProvider('')"></kx-multiselect-dropdown>
            <label>Provider*</label>
          </div>
          <div class="of-field">
            <kx-multiselect-dropdown [questions]="modelQuestion" [settings]="selectSettings" [data]="modelOptions"
              [placeholder]="'Select a model'" [ngModel]="modelId" name="modelId"
              (onSelect)="$event?.isManual && selectModel($event.id)" (onDeSelect)="selectModel('')"></kx-multiselect-dropdown>
            <label>Model*</label>
            <span class="of-hint">From the AI model master, the same list AI Prompt Master uses.</span>
          </div>
          <div class="of-field">
            <input type="password" class="of-control" autocomplete="new-password" [(ngModel)]="apiKey" name="apiKey"
                   [placeholder]="apiKeySaved ? 'Saved (hidden)' : 'Enter API key'">
            <label>API key</label>
            @if (apiKeyHint) {
              <span class="of-hint">{{ apiKeyHint }}</span>
            }
            @if (apiKeySaved) {
              <button type="button" class="text-btn" (click)="removeSavedKey()">Remove saved key</button>
            }
          </div>
          <div class="of-field">
            <input type="number" class="of-control" min="0" max="1" step="0.1" [(ngModel)]="temperature" name="temperature">
            <label>Temperature</label>
            <span class="of-hint">0 is focused and repeatable; 1 is more varied.</span>
          </div>
        }

        @if (stepKey === 'tools') {
          <section class="step-section wizard-grid__full">
            <h3 class="step-section__title">Add from the tool registry</h3>
            <div class="picker-row">
              <div class="of-field">
                <kx-multiselect-dropdown [questions]="toolQuestion" [settings]="toolSettings" [data]="toolSource"
                  [placeholder]="'Search the tool registry'" [ngModel]="registryToolPick" name="registryToolPick"
                  (onSelect)="$event?.isManual && pickRegistryTool($event)" (onDeSelect)="pickRegistryTool(null)"></kx-multiselect-dropdown>
                <label>Tool</label>
              </div>
              <button type="button" class="outline-btn" [disabled]="!registryToolPick" (click)="addToolFromRegistry()">Add tool</button>
            </div>
          </section>

          <section class="step-section wizard-grid__full">
            <h3 class="step-section__title">Agent tools <span class="count">{{ tools.length }}</span></h3>
            @for (tool of tools; track $index; let i = $index) {
              <div class="item-card">
                <div class="item-card__head">
                  <span class="method-chip">{{ tool.method || 'GET' }}</span>
                  <strong class="item-card__title">{{ tool.name || 'Tool ' + (i + 1) }}</strong>
                  <button type="button" class="icon-btn icon-btn--danger" title="Remove tool" [attr.aria-label]="'Remove ' + (tool.name || 'tool ' + (i + 1))" (click)="removeTool(i)">
                    <svg viewBox="0 0 24 24" aria-hidden="true"><path [attr.d]="iconPath('close')"></path></svg>
                  </button>
                </div>
                <div class="wizard-grid">
                  <div class="of-field">
                    <input type="text" class="of-control" placeholder="Invoice lookup" [(ngModel)]="tool.name" [name]="'toolName' + i" (ngModelChange)="onToolNameChange(tool)">
                    <label>Name</label>
                  </div>
                  <div class="of-field">
                    <kx-multiselect-dropdown [questions]="methodQuestion" [settings]="selectSettings" [data]="methodOptions"
                      [placeholder]="'Choose a method'" [(ngModel)]="tool.method" [name]="'toolMethod' + i"></kx-multiselect-dropdown>
                    <label>Method</label>
                  </div>
                  <div class="of-field wizard-grid__full">
                    <input type="text" class="of-control" placeholder="https://api.example.com/lookup" [(ngModel)]="tool.url" [name]="'toolUrl' + i">
                    <label>URL</label>
                  </div>
                  <div class="of-field wizard-grid__full">
                    <input type="text" class="of-control" placeholder="Look up an invoice by id" [(ngModel)]="tool.description" [name]="'toolDescription' + i">
                    <label>Description (tells the model when to use it)</label>
                  </div>
                </div>
              </div>
            } @empty {
              <p class="empty-note">No tools yet. Each tool becomes an HTTP request the model can make when it needs live data.</p>
            }
            <button type="button" class="outline-btn" (click)="addTool()">
              <svg viewBox="0 0 24 24" aria-hidden="true"><path [attr.d]="iconPath('add')"></path></svg>
              Add a custom HTTP tool
            </button>
          </section>
          <p class="field-note field-note--small wizard-grid__full">HTTP tools must be allowed for agent drafts on the workflow engine; saving tells you if they are not.</p>
        }

        @if (stepKey === 'knowledge') {
          <section class="step-section wizard-grid__full">
            <h3 class="step-section__title">Agent knowledge <span class="count">{{ knowledgeBases.length }}</span></h3>
            @for (kb of knowledgeBases; track kb.id; let i = $index) {
              <div class="kb-card" [attr.data-state]="knowledgeState(kb)">
                <div class="kb-card__head">
                  <div class="kb-card__name">
                    <strong>{{ kb.name }}</strong>
                    @if (kb.sourceType) {
                      <span class="kb-card__source">{{ knowledgeSourceLabel(kb) }}</span>
                    }
                  </div>
                  <span class="kb-chip">{{ knowledgeStateLabel(kb) }}</span>
                </div>
                @if (kb.description) {
                  <p class="kb-card__text">{{ kb.description }}</p>
                }
                @if (isBuilding(kb)) {
                  <div class="kb-progress" role="progressbar" aria-label="Building"><span></span></div>
                }
                @if (knowledgeProgress(kb)) {
                  <p class="kb-card__progress" aria-live="polite">{{ knowledgeProgress(kb) }}</p>
                }
                @if (kb.errorMessage && !isBuilding(kb)) {
                  <p class="kb-card__message">{{ kb.errorMessage }}</p>
                }
                <div class="kb-card__actions">
                  @if (kb.status === 'failed') {
                    <button type="button" class="text-btn" (click)="retryKnowledgeBase(kb)">Retry build</button>
                  }
                  <button type="button" class="text-btn text-btn--danger" (click)="removeKnowledgeBase(i)">Remove from agent</button>
                </div>
              </div>
            } @empty {
              <p class="empty-note">No knowledge bases yet. Each one becomes a search tool the model picks by its description, and answers cite what they found.</p>
            }
            @if (knowledgeBases.length) {
              <div class="form-check form-switch toggle-row">
                <input class="form-check-input" type="checkbox" role="switch" id="knowledgeOnly" [(ngModel)]="knowledgeOnly" name="knowledgeOnly">
                <label class="form-check-label" for="knowledgeOnly">Answer only from these knowledge bases</label>
              </div>
            }
          </section>

          <section class="step-section wizard-grid__full">
            <h3 class="step-section__title">Add an existing knowledge base</h3>
            <div class="picker-row">
              <div class="of-field">
                <kx-multiselect-dropdown [questions]="knowledgeQuestion" [settings]="toolSettings" [data]="knowledgeSource"
                  [placeholder]="'Search knowledge bases'" [ngModel]="knowledgePick" name="knowledgePick"
                  (onSelect)="$event?.isManual && pickKnowledgeBase($event)" (onDeSelect)="pickKnowledgeBase(null)"></kx-multiselect-dropdown>
                <label>Knowledge base</label>
              </div>
              <button type="button" class="outline-btn" [disabled]="!knowledgePick" (click)="addKnowledgeBase()">Add</button>
            </div>
          </section>

          <section class="step-section wizard-grid__full">
            <h3 class="step-section__title">Or create a new one</h3>
            @if (!newSource) {
              <div class="source-grid">
                @for (source of sourceChoices; track source.id) {
                  <button type="button" class="source-card" (click)="openNewSource(source.id)">
                    <span class="source-card__icon"><svg viewBox="0 0 24 24" aria-hidden="true"><path [attr.d]="iconPath(source.id)"></path></svg></span>
                    <span class="source-card__text">
                      <strong>{{ source.title }}</strong>
                      <small>{{ source.description }}</small>
                    </span>
                  </button>
                }
              </div>
            } @else {
              <div class="kb-new">
                <div class="kb-new__head">
                  <span class="source-card__icon"><svg viewBox="0 0 24 24" aria-hidden="true"><path [attr.d]="iconPath(newSource)"></path></svg></span>
                  <h4 class="kb-new__title">{{ newSource === 'website' ? 'New website knowledge base' : newSource === 'file' ? 'New file knowledge base'
                    : newSource === 'text' ? 'New text knowledge base' : 'New MongoDB knowledge base' }}</h4>
                  <button type="button" class="icon-btn" title="Cancel" aria-label="Cancel the new knowledge base" (click)="closeNewSource()">
                    <svg viewBox="0 0 24 24" aria-hidden="true"><path [attr.d]="iconPath('close')"></path></svg>
                  </button>
                </div>
                <div class="wizard-grid kb-new__body">
                  @if (knowledgeError) {
                    <p class="error-banner wizard-grid__full" role="alert">{{ knowledgeError }}</p>
                  }
                  @if (newSource === 'website') {
                    <div class="of-field of-field--area wizard-grid__full">
                      <textarea class="of-control" rows="2" placeholder="https://www.example.com/" [(ngModel)]="knowledgeForm.websiteUrls"
                                name="kbWebsiteUrls" (ngModelChange)="onWebsiteUrlsChange()"></textarea>
                      <label>Start URLs*</label>
                      <span class="of-hint">One per line. Only pages on these sites are crawled, and robots.txt is respected.</span>
                    </div>
                  }
                  @if (newSource === 'file') {
                    <div class="kb-files wizard-grid__full">
                      <label class="kb-files__drop">
                        <svg viewBox="0 0 24 24" aria-hidden="true"><path [attr.d]="iconPath('file')"></path></svg>
                        <span class="kb-files__cta">Choose files or drop them here</span>
                        <span class="of-hint">Up to 20 files, 15 MB each: PDF, Word (.docx), Excel (.xlsx), CSV, HTML or text. Scanned PDFs have no text to read.</span>
                        <input type="file" multiple [accept]="fileFormats" (change)="onKnowledgeFilesChosen($event)">
                      </label>
                      @for (file of knowledgeForm.files; track file.documentId; let f = $index) {
                        <div class="kb-files__row">
                          <svg viewBox="0 0 24 24" aria-hidden="true"><path [attr.d]="iconPath('file')"></path></svg>
                          <span class="kb-files__name">{{ file.documentName }}</span>
                          <button type="button" class="text-btn text-btn--danger" (click)="removeKnowledgeFile(f)">Remove</button>
                        </div>
                      }
                      @if (uploadingFiles) {
                        <p class="field-note field-note--small" aria-live="polite">Uploading {{ uploadingFiles }} file{{ uploadingFiles === 1 ? '' : 's' }}…</p>
                      }
                    </div>
                  }
                  <div class="of-field wizard-grid__full">
                    <input type="text" class="of-control" placeholder="Product documentation" [(ngModel)]="knowledgeForm.name"
                           name="kbName" (ngModelChange)="onKnowledgeNameChange()">
                    <label>Name*</label>
                  </div>
                  <div class="of-field of-field--area wizard-grid__full">
                    <textarea class="of-control" rows="2" placeholder="What it holds, e.g. resolved helpdesk tickets with their fixes"
                              [(ngModel)]="knowledgeForm.description" name="kbDescription" (ngModelChange)="onKnowledgeDescriptionChange()"></textarea>
                    <label>Description*</label>
                    <span class="of-hint">The agent picks which knowledge base to search by this.</span>
                  </div>
                  @if (newSource === 'website') {
                    <details class="kb-advanced wizard-grid__full">
                      <summary>Crawl settings</summary>
                      <div class="wizard-grid">
                        <div class="of-field">
                          <input type="number" class="of-control" min="0" max="3" [(ngModel)]="knowledgeForm.maxDepth" name="kbMaxDepth">
                          <label>Link depth</label>
                          <span class="of-hint">How many links away from the start pages to follow, 0 to 3.</span>
                        </div>
                        <div class="of-field">
                          <input type="number" class="of-control" min="1" max="500" [(ngModel)]="knowledgeForm.maxPages" name="kbMaxPages">
                          <label>Max pages</label>
                          <span class="of-hint">Up to 500. More pages take longer to build.</span>
                        </div>
                        <div class="of-field">
                          <input type="text" class="of-control" placeholder="*/docs/*" [(ngModel)]="knowledgeForm.includePatterns" name="kbIncludePatterns">
                          <label>Only URLs matching</label>
                        </div>
                        <div class="of-field">
                          <input type="text" class="of-control" placeholder="*/blog/*, */tag/*" [(ngModel)]="knowledgeForm.excludePatterns" name="kbExcludePatterns">
                          <label>Skip URLs matching</label>
                        </div>
                        <div class="of-field">
                          <input type="number" class="of-control" min="0" [(ngModel)]="knowledgeForm.refreshHours" name="kbRefreshHours">
                          <label>Re-crawl every (hours)</label>
                          <span class="of-hint">0 re-crawls only when the knowledge base is saved again.</span>
                        </div>
                      </div>
                    </details>
                  } @else if (newSource === 'text') {
                    <div class="of-field of-field--area wizard-grid__full">
                      <textarea class="of-control" rows="10" placeholder="Paste a policy, FAQ or any text the agent should answer from"
                                [(ngModel)]="knowledgeForm.textContent" name="kbTextContent"></textarea>
                      <label>Text*</label>
                      <span class="of-hint">Up to {{ maxTextChars.toLocaleString() }} characters ({{ knowledgeForm.textContent.length.toLocaleString() }} now). Answers cite it by this knowledge base's name.</span>
                    </div>
                  } @else if (newSource === 'mongodb') {
                    <div class="of-field wizard-grid__full">
                      <input type="password" class="of-control" autocomplete="new-password" placeholder="mongodb+srv://user:pass@cluster.mongodb.net"
                             [(ngModel)]="knowledgeForm.connectionString" name="kbConnectionString">
                      <label>Connection string*</label>
                      <span class="of-hint">Stored encrypted and never shown again.</span>
                    </div>
                    <div class="of-field">
                      <input type="text" class="of-control" placeholder="helpdesk" [(ngModel)]="knowledgeForm.dbName" name="kbDbName">
                      <label>Database*</label>
                    </div>
                    <div class="of-field">
                      <input type="text" class="of-control" placeholder="tickets" [(ngModel)]="knowledgeForm.collectionName" name="kbCollectionName">
                      <label>Collection*</label>
                    </div>
                    <div class="of-field">
                      <input type="text" class="of-control" placeholder="subject,description,resolution" [(ngModel)]="knowledgeForm.fields" name="kbFields">
                      <label>Fields to embed*</label>
                      <span class="of-hint">Comma-separated source fields; a dotted path reads inside records and lists, e.g. module_list.module_name.</span>
                    </div>
                    <div class="of-field">
                      <input type="text" class="of-control" placeholder="subject" [(ngModel)]="knowledgeForm.titleField" name="kbTitleField">
                      <label>Title field</label>
                      <span class="of-hint">Titles each document; leave empty to pick one by name.</span>
                    </div>
                    <div class="of-field of-field--area wizard-grid__full">
                      <textarea class="of-control" rows="2" placeholder='{"status": "closed"}' [(ngModel)]="knowledgeForm.filter" name="kbFilter"></textarea>
                      <label>Filter (JSON)</label>
                      <span class="of-hint">Leave empty to embed every document.</span>
                    </div>
                  }
                </div>
                <div class="kb-new__actions">
                  <span class="field-note field-note--small">It starts building as soon as it is created.</span>
                  <button type="button" class="text-btn" (click)="closeNewSource()">Cancel</button>
                  <button type="button" class="primary-btn" [disabled]="creatingKnowledge || uploadingFiles > 0" (click)="createKnowledgeBase()">
                    {{ creatingKnowledge ? 'Creating…' : 'Create and add' }}
                  </button>
                </div>
              </div>
            }
          </section>

          <p class="callout wizard-grid__full">Builds run on the server: keep going through the steps, or leave this page, and Knowledge Bases shows the same progress. Publishing waits until every knowledge base is Ready, and an account copy can use only global knowledge bases and its own account's.</p>
          @if (copyMode && knowledgeNotReady.length) {
            <p class="callout callout--warning wizard-grid__full">An account copy runs as soon as it is saved, so Next saves it only once every knowledge base is Ready.</p>
          }
        }

        @if (triggersVisited) {
          <div class="wizard-grid__full" [hidden]="stepKey !== 'triggers'">
            <app-agent-trigger-editor [setupId]="draftId" [readOnly]="copyMode"></app-agent-trigger-editor>
          </div>
        }

        @if (stepKey === 'guardrails') {
          <div class="setting-card wizard-grid__full" [class.setting-card--on]="guardrailsEnabled">
            <div class="setting-card__row">
              <label class="setting-card__text" for="guardrailsEnabled">
                <strong>Enable guardrails</strong>
                <span>Checks every message before the agent sees it and refuses prompt-injection attempts and empty or oversized input.</span>
              </label>
              <div class="form-check form-switch">
                <input class="form-check-input" type="checkbox" role="switch" id="guardrailsEnabled" [(ngModel)]="guardrailsEnabled" name="guardrailsEnabled">
              </div>
            </div>
            @if (guardrailsEnabled) {
              <div class="setting-card__body wizard-grid">
                <div class="of-field">
                  <input type="text" class="of-control" placeholder="default" [(ngModel)]="guardrailsConfigId" name="guardrailsConfigId">
                  <label>Guardrails config ID</label>
                </div>
              </div>
            }
          </div>
        }

        @if (stepKey === 'memory') {
          <div class="setting-card wizard-grid__full" [class.setting-card--on]="memoryEnabled">
            <div class="setting-card__row">
              <label class="setting-card__text" for="memoryEnabled">
                <strong>Remember earlier turns in this conversation</strong>
                <span>Remembers up to the set number of prior exchanges per session, so the agent has conversational context on follow-up messages.</span>
              </label>
              <div class="form-check form-switch">
                <input class="form-check-input" type="checkbox" role="switch" id="memoryEnabled" [(ngModel)]="memoryEnabled" name="memoryEnabled">
              </div>
            </div>
            @if (memoryEnabled) {
              <div class="setting-card__body wizard-grid">
                <div class="of-field">
                  <input type="text" class="of-control" placeholder="conversation" [(ngModel)]="memoryKey" name="memoryKey">
                  <label>Memory key</label>
                  <span class="of-hint">Leave blank to use "conversation". Memory is always kept per user and session.</span>
                </div>
                <div class="of-field">
                  <input type="number" class="of-control" min="1" [(ngModel)]="memoryMaxEntries" name="memoryMaxEntries">
                  <label>Max remembered turns</label>
                </div>
              </div>
            }
          </div>
        }

        @if (stepKey === 'test' && copyMode) {
          <p class="callout wizard-grid__full">This is an account's copy of a published version. Run it from the Account agents list after saving.</p>
        }
        @if (stepKey === 'test' && !copyMode) {
          @if (knowledgeNotReady.length) {
            <p class="callout callout--warning wizard-grid__full">Not ready yet: {{ knowledgeNotReadyNames }}. The test can search a knowledge base only once it has been built.</p>
          }
          <div class="test-console wizard-grid__full">
            <div class="of-field of-field--area of-field--flush">
              <textarea class="of-control" rows="4" [(ngModel)]="testMessage" name="testMessage"
                        placeholder="Ask the agent something to try it before publishing."></textarea>
              <label>Test message</label>
            </div>
            @if (triggerEditor && triggerEditor.triggers.length) {
              <div class="chip-row">
                <span class="chip-row__label">Use a trigger's latest event:</span>
                @for (t of triggerEditor.triggers; track $index; let i = $index) {
                  <button type="button" class="chip-btn" [disabled]="sampling" (click)="useTriggerSample(t)">{{ t.name || 'Trigger ' + (i + 1) }}</button>
                }
              </div>
            }
            <div class="test-console__bar">
              <span class="field-note field-note--small">Runs the current draft. Nothing is published.</span>
              <button type="button" class="primary-btn" [disabled]="testing || !testMessage.trim()" (click)="runTest()">
                <svg viewBox="0 0 24 24" aria-hidden="true"><path [attr.d]="iconPath('play')"></path></svg>
                {{ testing ? 'Running…' : 'Test agent' }}
              </button>
            </div>
          </div>
          @if (testResult) {
            <div class="test-result wizard-grid__full" aria-live="polite">
              <span class="test-result__label">Agent response</span>
              <div class="test-result__body">{{ testResult }}</div>
            </div>
          }
        }

        @if (stepKey === 'publish') {
          @if (knowledgeNotReady.length) {
            <p class="error-banner wizard-grid__full">Waiting for knowledge bases to finish building: {{ knowledgeNotReadyNames }}. {{ copyMode ? 'Saving' : 'Publishing' }} is possible once every one is Ready; a failed one can be retried or removed in the Knowledge step.</p>
          }
          <dl class="review wizard-grid__full">
            <div class="review__row">
              <dt>Agent</dt>
              <dd>{{ displayName || '—' }} @if (agentName) {<code>{{ agentName }}</code>}</dd>
              <button type="button" class="text-btn" (click)="openStep('identity')">Edit</button>
            </div>
            <div class="review__row">
              <dt>Domain</dt>
              <dd>{{ domainLabel || '—' }}</dd>
              <button type="button" class="text-btn" (click)="openStep('identity')">Edit</button>
            </div>
            <div class="review__row">
              <dt>Model</dt>
              <dd>{{ providerName && modelName ? providerName + ' · ' + modelName : '—' }}</dd>
              <button type="button" class="text-btn" (click)="openStep('model')">Edit</button>
            </div>
            <div class="review__row">
              <dt>Tools</dt>
              <dd>{{ tools.length ? tools.length + ' tool' + (tools.length === 1 ? '' : 's') : 'None' }}</dd>
              <button type="button" class="text-btn" (click)="openStep('tools')">Edit</button>
            </div>
            <div class="review__row">
              <dt>Knowledge</dt>
              <dd>{{ knowledgeBases.length ? knowledgeBases.length + ' knowledge base' + (knowledgeBases.length === 1 ? '' : 's') : 'None' }}</dd>
              <button type="button" class="text-btn" (click)="openStep('knowledge')">Edit</button>
            </div>
            @if (!copyMode) {
              <div class="review__row">
                <dt>Triggers</dt>
                <dd>{{ triggerCount ? triggerCount + ' trigger' + (triggerCount === 1 ? '' : 's') : 'None' }}</dd>
                <button type="button" class="text-btn" (click)="openStep('triggers')">Edit</button>
              </div>
            }
            <div class="review__row">
              <dt>Guardrails</dt>
              <dd>{{ guardrailsEnabled ? 'On' : 'Off' }}</dd>
              <button type="button" class="text-btn" (click)="openStep('guardrails')">Edit</button>
            </div>
            <div class="review__row">
              <dt>Memory</dt>
              <dd>{{ memoryEnabled ? 'On' : 'Off' }}</dd>
              <button type="button" class="text-btn" (click)="openStep('memory')">Edit</button>
            </div>
          </dl>
          @if (copyMode) {
            <p class="callout wizard-grid__full"><strong>Account copy</strong> of {{ domainLabel }} version <strong>{{ version }}</strong>. Saving changes only this copy; the master and other accounts are not affected.</p>
          } @else {
            @if (triggerCount > 0) {
              <p class="callout wizard-grid__full">Draft triggers go live when this version is published (unless "Enable when the agent is published" is off); each account runs its own active copy of the agent.</p>
            }
            @if (previouslyPublishedVersion) {
              <p class="callout callout--warning wizard-grid__full">This draft was already published as <strong>{{ previouslyPublishedVersion }}</strong>. Versions are immutable, so enter a new version below to publish an updated one.</p>
            }
            <div class="of-field">
              <input type="text" class="of-control" placeholder="v2" [(ngModel)]="version" name="version">
              <label>Version*</label>
            </div>
            <div class="of-field">
              <kx-multiselect-dropdown [questions]="lifecycleQuestion" [settings]="selectSettings" [data]="lifecycleOptions"
                [placeholder]="'Choose a lifecycle'" [(ngModel)]="lifecycle" name="lifecycle"></kx-multiselect-dropdown>
              <label>Lifecycle*</label>
            </div>
          }
        }
      </div>

      <footer class="wizard__actions">
        <button type="button" class="text-btn" (click)="cancel()">Cancel</button>
        <div class="wizard__actions-main">
          <button type="button" class="outline-btn" [disabled]="step === 1" (click)="back()">Back</button>
          @if (step < stepTitles.length) {
            <button type="submit" class="primary-btn" [disabled]="saving">
              {{ saving ? 'Saving…' : 'Next' }}
              @if (!saving) {
                <svg viewBox="0 0 24 24" aria-hidden="true"><path [attr.d]="iconPath('next')"></path></svg>
              }
            </button>
          } @else {
            @if (copyMode) {
              <button type="button" class="primary-btn" [disabled]="!validFinalStep || saving || knowledgeNotReady.length > 0" (click)="saveCopy()">
                {{ saving ? 'Saving…' : 'Save copy' }}
              </button>
            } @else {
              <button type="button" class="primary-btn" [disabled]="!validFinalStep || publishing || saving || knowledgeNotReady.length > 0" (click)="publish()">
                {{ publishing ? 'Publishing…' : 'Create & publish agent' }}
              </button>
            }
          }
        </div>
      </footer>
    </form>
  </div>

</div>
